import { FunctionComponent, useMemo } from "react"
import { useTypedSelector } from "../../app/store"
import {
  useGetOrganizationResultsAccessStatusQuery,
  useGetOrganizationUserMeasureScoresQuery,
} from "../../app/services/meseeknaApiV2"
import { useNavigate } from "react-router-dom"
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js"
import { Bar } from "react-chartjs-2"
import type { ChartConfiguration } from "chart.js"

import { getMeasureColor } from "./measureColors"

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

/** Convert hex color to a softer version by adding alpha transparency */
const softenColor = (hex: string, alpha = 0.55): string => {
  const r = parseInt(hex.slice(1, 3), 16)
  const g = parseInt(hex.slice(3, 5), 16)
  const b = parseInt(hex.slice(5, 7), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

const PerformanceSection: FunctionComponent = () => {
  const organization = useTypedSelector((state) => state.authSlice.organization)
  const orgId = Number(organization?.id) || 0
  const navigate = useNavigate()

  const { data: accessStatusData, isLoading: isLoadingAccess } =
    useGetOrganizationResultsAccessStatusQuery(orgId, { skip: !orgId })

  const isUnlocked = accessStatusData?.data?.hasAccess ?? false

  const { data: scoresData, isLoading: isLoadingScores } =
    useGetOrganizationUserMeasureScoresQuery(orgId, { skip: !orgId || !isUnlocked })

  // Build chart data
  const { chartData, chartOptions, measureNames, userData } = useMemo(() => {
    if (!scoresData?.data || scoresData.data.length === 0) {
      return { chartData: null, chartOptions: null, measureNames: [], userData: [] }
    }

    const users = scoresData.data
    // Get unique measures from the first user (all users have same measures)
    const measures = users[0]?.measures || []

    const names = users.map(
      (u) => `${u.firstName || ""} ${u.lastName || ""}`.trim() || u.email.split("@")[0],
    )

    const datasets = measures.map((m) => ({
      label: m.measureName,
      backgroundColor: softenColor(getMeasureColor(m.measureName, m.measureColor)),
      data: users.map((u) => {
        const userMeasure = u.measures.find((um) => um.measureId === m.measureId)
        return userMeasure?.score || 0
      }),
    }))

    const maxTotal = Math.max(
      ...users.map((u) => u.measures.reduce((sum, m) => sum + m.score, 0)),
    )

    const data: ChartConfiguration<"bar">["data"] = {
      labels: names,
      datasets,
    }

    const options: ChartConfiguration<"bar">["options"] = {
      scales: {
        x: {
          stacked: true,
          grid: { display: false },
          ticks: { display: users.length <= 15 },
        },
        y: {
          stacked: true,
          type: "linear",
        },
      },
      plugins: {
        legend: {
          position: "bottom",
          labels: { usePointStyle: true, padding: 16 },
        },
        title: { display: false },
        annotation: {
          annotations: {
            maxLine: {
              type: "line" as const,
              yMin: maxTotal,
              yMax: maxTotal,
              borderColor: "#d1d5db",
              borderWidth: 1,
              borderDash: [4, 4],
            },
          },
        },
      },
      interaction: { mode: "index" as const },
      responsive: true,
      maintainAspectRatio: false,
    }

    return {
      chartData: data,
      chartOptions: options,
      measureNames: measures.map((m) => m.measureName),
      userData: users,
    }
  }, [scoresData])

  // Loading state
  if (isLoadingAccess || (isUnlocked && isLoadingScores)) {
    return (
      <div className="min-h-full bg-gray-50 py-12 px-6">
        <div className="max-w-6xl mx-auto flex justify-center items-center py-24">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4d86ff]"></div>
        </div>
      </div>
    )
  }

  // Locked state
  if (!isUnlocked) {
    return (
      <div className="min-h-full bg-gray-50 py-12 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 8v-2a4 4 0 00-8 0v2m-2 0h12a2 2 0 012 2v8a2 2 0 01-2 2H6a2 2 0 01-2-2v-8a2 2 0 012-2z" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Track Cognitive Patterns Over Time</h2>
            <p className="text-gray-500 max-w-md mx-auto mb-6">
              This view shows how scores compare and evolve. Once you have completed simulations, you&apos;ll see which cognitive strengths emerge across your participant pool.
            </p>
            <button
              onClick={() => navigate("/dashboard/recruiter-admin/users/all")}
              className="px-6 py-2.5 text-white text-sm font-medium rounded-lg transition-colors hover:opacity-90"
              style={{ backgroundColor: "#6182F7" }}
            >
              Deploy Simulation
            </button>
          </div>
        </div>
      </div>
    )
  }

  // No data state
  if (!chartData || !userData || userData.length === 0) {
    return (
      <div className="min-h-full bg-gray-50 py-12 px-6">
        <div className="max-w-6xl mx-auto">
          <h1 className="text-2xl font-semibold text-gray-900 mb-6">Cognitive Blueprints</h1>
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <p className="text-gray-500">No simulation data available yet. Scores will appear here once team members complete their simulations.</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-full bg-gray-50 py-8 px-6">
      <div className="max-w-6xl mx-auto">
        <div className="mb-6">
          <h1 className="text-2xl font-semibold text-gray-900">Cognitive Blueprints</h1>
          <p className="text-sm text-gray-500 mt-1">
            Visualize how your team thinks. Each bar shows individual cognitive measure scores per participant.
          </p>
        </div>

        {/* Chart */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
          <div style={{ height: "380px", width: "100%" }}>
            <Bar data={chartData} options={chartOptions} />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Name</th>
                  <th className="text-left px-4 py-3 font-medium text-gray-600">Email</th>
                  {measureNames.map((name) => (
                    <th key={name} className="text-center px-3 py-3 font-medium text-gray-600 whitespace-nowrap">
                      {name}
                    </th>
                  ))}
                  <th className="text-center px-4 py-3 font-medium text-gray-600">Total</th>
                </tr>
              </thead>
              <tbody>
                {userData.map((user) => {
                  const total = user.measures.reduce((sum, m) => sum + m.score, 0)
                  return (
                    <tr key={user.userId} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium text-gray-900">
                        {`${user.firstName || ""} ${user.lastName || ""}`.trim() || "—"}
                      </td>
                      <td className="px-4 py-3 text-gray-500">{user.email}</td>
                      {measureNames.map((name) => {
                        const m = user.measures.find((um) => um.measureName === name)
                        const score = m?.score || 0
                        return (
                          <td key={name} className="text-center px-3 py-3 font-semibold text-gray-900">
                            {score}
                          </td>
                        )
                      })}
                      <td className="text-center px-4 py-3 font-bold text-gray-900">{total}</td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PerformanceSection
